import React, { useMemo, useState } from 'react';
import i18next from 'i18next';
import { platformService } from '../../services/platform.service';
import { appState } from '../../../AppState';
import { CategoryOption } from './inlineCategoryOptions';
import { InlineMultiListCell, committing } from './InlineFieldCells';
import { PillList } from './PillList';
import { useCreateTag } from './useCreateTag';

export interface TagRecord {
  Id: number;
  Name: string;
  IsDeleted?: boolean;
}

interface Props {
  ticketId: number;
  /** The ticket's tags as the row holds them. */
  tags: TagRecord[];
  resting: React.ReactElement;
  onSaved: () => void | Promise<void>;
  /** Puts the saved tags into the row at once, so the cell shows them and goes green before the reload. */
  onPatch?: (tags: TagRecord[]) => void;
  guard?: () => Promise<void>;
  /** Every tag to choose from, where the page has not put them in app state (the user page's Tickets tab). */
  knownTags?: TagRecord[];
}

/** The Tags cell at rest: the ticket's tags as pills, in name order. */
export const TagPills = ({ tags }: { tags: TagRecord[] | undefined }) => {
  const names = useMemo(() => (tags ?? []).map(tag => tag.Name).sort((a, b) => a.localeCompare(b)), [tags]);
  return <PillList names={names} title={i18next.t('ticket.add-ticket.tag')} />;
};

/** An OData action answering `false` instead of failing - AddTags and RemoveTags catch everything and do that. */
const refused = (response: { data?: { value?: unknown } }) => response?.data?.value === false;

/**
 * A ticket's tags, edited in a panel of chips as Collaborators are. Typing a name no tag has offers to make it, as
 * the ticket page's "Add Tag?" does, and the new tag is put on the ticket (Shaun, 2026-10-13). Saved as the ticket
 * page saves: removed tags by name (RemoveTags), added ones by id (AddTags).
 */
export const InlineTagsCell = ({ ticketId, tags, resting, onSaved, onPatch, guard, knownTags }: Props) => {
  const appTags = appState().tags as TagRecord[];
  const allTags = knownTags ?? appTags ?? [];
  const createTag = useCreateTag();
  /** Tags made from this cell, kept until the app's own list has them. */
  const [made, setMade] = useState<TagRecord[]>([]);
  const known = useMemo(
    () => [...allTags, ...made.filter(tag => !allTags.some(other => other.Id === tag.Id))].filter(tag => !tag.IsDeleted),
    [allTags, made]
  );
  const options: CategoryOption[] = useMemo(
    () => known.map(tag => ({ id: tag.Id, name: tag.Name })).sort((a, b) => a.name.localeCompare(b.name)),
    [known]
  );
  const valueIds = tags.map(tag => tag.Id);

  const save = async (ids: number[]) => {
    const api = new platformService();
    const removed = tags.filter(tag => !ids.includes(tag.Id));
    const added = ids.filter(id => !valueIds.includes(id));
    if (removed.length && refused(await api.removeRefTags(ticketId, removed.map(tag => tag.Name).join(','))))
      throw new Error(`RemoveTags answered false for ticket ${ticketId}`);
    if (added.length && refused(await api.udpateTicketTags(ticketId, added.join(','))))
      throw new Error(`AddTags answered false for ticket ${ticketId}`);
    onPatch?.(ids.map(id => known.find(tag => tag.Id === id)).filter((tag): tag is TagRecord => !!tag));
  };

  const create = async (name: string): Promise<CategoryOption | null> => {
    const tag = await createTag(name);
    if (!tag) return null;
    setMade(prev => [...prev, tag]);
    return { id: tag.Id, name: tag.Name };
  };

  return (
    <InlineMultiListCell
      rowKey={ticketId}
      label={i18next.t('ticket.add-ticket.tag')}
      options={options}
      valueIds={valueIds}
      resting={resting}
      commit={committing<number[]>(save, onSaved, guard)}
      onCreate={create}
      createLabel={name => `${i18next.t('ticket.add-ticket.tags.new-tag')} '${name}'`}
      sortChips
    />
  );
};
